import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Clock3,
  Loader2,
  Rocket,
  Sparkles,
} from "lucide-react";
import HorizontalList from "../horizontalList";
import empresasHero from "../../assets/EmpresasInicio2.jpg";
import { useEmpresasLanguage } from "../../context/EmpresasLanguageContext";

const estadoRoadmapMeta = {
  completado: {
    icon: CheckCircle2,
    clase: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
  },
  activo: {
    icon: Loader2,
    clase: "text-[#1746a2] bg-[#1746a2]/10 border-[#1746a2]/25",
  },
  pendiente: {
    icon: Circle,
    clase: "text-slate-400 bg-slate-100 border-slate-200",
  },
};

export default function EmpresaProductoProximamente({ config }) {
  const { t } = useEmpresasLanguage();
  const ui = t.productoProximamente;

  if (!config) return null;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <section className="relative bg-[#0b1f3f] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url(${empresasHero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1f3f] via-[#123a72] to-[#1746a2]" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#dbeafe]">
              <Sparkles className="w-3.5 h-3.5" />
              {ui.enDesarrollo}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold text-amber-100">
              <Clock3 className="w-3.5 h-3.5" />
              {ui.disponibleProximamente}
            </span>
          </div>

          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.35em] text-[#9ec5ff] mb-4">
            {config.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl">
            {config.titulo}
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-[#dbeafe] max-w-2xl font-medium leading-relaxed">
            {config.subtitulo}
          </p>

          <div className="mt-10 max-w-xl rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4 mb-3">
              <p className="text-sm font-semibold text-[#dbeafe]">{ui.avanceProducto}</p>
              <p className="text-2xl font-extrabold text-white">{config.progreso}%</p>
            </div>
            <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#60a5fa] to-[#93c5fd] transition-all duration-700"
                style={{ width: `${config.progreso}%` }}
              />
            </div>
            <p className="mt-3 text-sm text-[#bfdbfe]">{config.faseActual}</p>
          </div>
        </div>
      </section>

      <HorizontalList items={config.navItems} even plain />

      <section id="vision" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">
              {ui.visionLabel}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f] leading-snug">
              {ui.visionTitle}
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{config.descripcion}</p>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">{ui.visionExtra}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3 gap-4">
            {config.highlights.map(({ valor, etiqueta, detalle }) => (
              <article
                key={etiqueta}
                className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm"
              >
                <p className="text-3xl font-extrabold text-[#1746a2]">{valor}</p>
                <p className="mt-1 text-sm font-bold uppercase tracking-wide text-[#0b1f3f]">
                  {etiqueta}
                </p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{detalle}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="funcionalidades" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#e8f2f4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">
              {ui.featuresLabel}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">
              {ui.featuresTitle}
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">{ui.featuresNote}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {config.funcionalidades.map(({ icon: Icon, titulo, texto }) => (
              <article
                key={titulo}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-11 h-11 rounded-xl bg-[#1746a2]/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#1746a2]" />
                </div>
                <h3 className="text-xl font-extrabold text-[#0b1f3f]">{titulo}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{texto}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="roadmap" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">
              {ui.roadmapLabel}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">
              {ui.roadmapTitle}
            </h2>
          </div>

          <ol className="space-y-4">
            {config.roadmap.map(({ fase, estado, detalle }, index) => {
              const meta = estadoRoadmapMeta[estado];
              const Icon = meta.icon;
              const etiqueta = ui.estados[estado];
              return (
                <li
                  key={fase}
                  className={`flex gap-4 rounded-2xl border p-5 ${meta.clase}`}
                >
                  <div className="shrink-0 mt-0.5">
                    <Icon className={`w-5 h-5 ${estado === "activo" ? "animate-spin" : ""}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider opacity-70">
                        {ui.etapa} {index + 1}
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/60">
                        {etiqueta}
                      </span>
                    </div>
                    <h3 className="mt-1 text-lg font-extrabold text-[#0b1f3f]">{fase}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{detalle}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section id="contacto" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#0b1f3f] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1f3f] via-[#123a72] to-[#1746a2] opacity-90" />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#dbeafe] mb-4">
                <Rocket className="w-4 h-4" />
                {ui.earlyAccess}
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold leading-snug">{ui.contactTitle}</h2>
              <p className="mt-4 text-[#dbeafe] text-lg leading-relaxed">{ui.contactBody}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={config.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 text-[#123a72] font-bold hover:bg-[#e8f2ff] transition"
                >
                  {config.whatsappLabel}
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/empresas"
                  className="inline-flex items-center justify-center rounded-full border-2 border-white/80 px-8 py-3 font-bold hover:bg-white/10 transition"
                >
                  {ui.volverEmpresas}
                </Link>
              </div>
            </div>

            <div className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 sm:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#9ec5ff] mb-4">
                {ui.yaDisponible}
              </p>
              <ul className="space-y-3">
                {ui.productosDisponibles.map(({ titulo, path }) => (
                  <li key={path}>
                    <Link
                      to={path}
                      className="flex items-center justify-between rounded-2xl bg-white/10 px-5 py-4 font-semibold hover:bg-white/15 transition group"
                    >
                      {titulo}
                      <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
